import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { useTranslation } from "react-i18next";

import { colors, transition } from "../../../styles/variables";
import breakpoints from "../../../styles/breakpoints";
import {
  trackingEvents,
  trackingEventSelectedProductRatingMethods,
  useTrackEvent,
} from "../../../helpers/Analytics";
import { I18N_NAMESPACES } from "../../../helpers/constants";
import loaderIcon from "../../../assets/images/icons/loader.svg";
import { MAX_RATING, MIN_RATING, formatRatingScore, positionToHalfStarScore } from "../../../helpers/ratings";

/* Hold this long (finger down, still) before a slide scrubs half stars.
   Anything that moves sooner is a scroll flick and stays with the browser —
   which is what lets the strip live inside horizontal carousels without
   swipes committing accidental ratings. Kept short: longer holds felt laggy
   to engage, and the movement-slop check below already filters flicks. The
   floor is scroll capture: below this, deliberate slow carousel drags start
   to engage the scrub instead of scrolling. */
const SCRUB_HOLD_MS = 100;
/* Movement beyond this before the hold elapses abandons the hold: the finger
   is scrolling, not pressing. */
const HOLD_CANCEL_SLOP_PX = 8;
/* How far beyond the strip's ends a tap or click still counts as a rating.
   Anything further into the row is a dead zone: absorbed, but committing
   nothing — not a surprise 0.5 or 5. */
const TAP_SLOP_PX = 20;
/* One duration for the whole save cross-fade: the spinner fades in as the
   stars dim, and both reverse together when the save resolves. */
const SAVE_FADE_MS = 250;
/* Fast saves would otherwise flash the spinner for a few frames; once shown,
   it stays up at least this long so the feedback reads as deliberate. */
const MIN_SAVE_INDICATOR_MS = 600;

/* Same star geometry as assets/images/icons/start-fill.svg, shifted to a 0-based origin
   so the filled and outlined states share one path and a half fill lines up exactly. */
const STAR_PATH = [
  "m12.5 18.75-7.3473157 3.8627124 1.4032125-8.1813562-5.9441033-5.79406863",
  "8.2145487-1.19364378 3.6736578-7.44364379 3.673658 7.44364379 8.214548",
  "1.19364378-5.944103 5.79406863 1.403213 8.1813562z",
].join(" ");

interface StarSizeProps {
  $large: boolean;
  $medium: boolean;
  $inAllWines: boolean;
}

const getStarSize = ({ $large, $medium }: StarSizeProps): string => {
  if ($large) {
    return "40px";
  }
  if ($medium) {
    return "25px";
  }
  return "15px";
};

const getStarMargin = ({ $large, $medium, $inAllWines }: StarSizeProps): string => {
  if ($large) {
    return "2px 4px";
  }
  if ($medium) {
    return "0 2px";
  }
  if ($inAllWines) {
    return "0 1px";
  }
  return "0px 2px";
};

const Row = styled.div<{ $interactive: boolean; $pending: boolean }>`
  position: relative;
  /* Both pan axes stay native so quick flicks scroll carousels and the page;
     the scrub claims the gesture only after the hold, via preventDefault on
     touchmove. (manipulation also drops the double-tap-zoom click delay.) */
  touch-action: manipulation;
  -webkit-user-select: none;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  cursor: ${(props) => (props.$interactive ? "pointer" : "default")};
  ${(props) =>
    props.$interactive &&
    css`
      /* Forgiving hit zone: near-misses land here (and rate) instead of falling
         through to the tray-opening links that surround the strip on the cards.
         Painted after those links in the DOM, so it wins the overlap. */
      &::before {
        content: "";
        position: absolute;
        inset: -12px -4px;
      }

      &:focus-visible {
        outline: 2px solid ${colors.black};
        outline-offset: 4px;
      }
    `}
`;

const spinnerRotate = keyframes`
  from {
    transform: rotateZ(0);
  }
  to {
    transform: rotateZ(360deg);
  }
`;

/* Overlaid on the strip's centre rather than beside it — the narrow dashboard
   cards leave no room to the side, so a side spinner floated over the next
   product. Always mounted and cross-faded via opacity so stars and spinner
   swap gradually instead of jumping. The translucent chip keeps the black
   arc legible over filled stars. */
const SavingSpinner = styled.span<{ $large: boolean; $visible: boolean }>`
  position: absolute;
  inset: 0;
  margin: auto;
  width: ${(props) => (props.$large ? "26px" : "20px")};
  height: ${(props) => (props.$large ? "26px" : "20px")};
  border-radius: 50%;
  background-color: ${colors.white};
  background-image: url("${loaderIcon}");
  background-repeat: no-repeat;
  background-size: 80% 80%;
  background-position: center;
  pointer-events: none;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  /* visibility flips only once the fade completes, and pauses the rotation
     so hidden spinners cost nothing between saves. */
  visibility: ${(props) => (props.$visible ? "visible" : "hidden")};
  transition: opacity ${SAVE_FADE_MS}ms ease, visibility ${SAVE_FADE_MS}ms;
  animation: linear ${spinnerRotate} 1s infinite;
  animation-play-state: ${(props) => (props.$visible ? "running" : "paused")};
`;

/* Anchors the saving spinner to the stars themselves — the Row spans its
   container's full width, so the strip (not the Row) is the reference box. */
const StarStrip = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
`;

const StarSlot = styled.span<StarSizeProps & { $dimmed: boolean }>`
  position: relative;
  display: inline-block;
  width: ${getStarSize};
  height: ${getStarSize};
  margin: ${getStarMargin};
  /* The stars' half of the save cross-fade: they recede as the spinner
     fades in over them. */
  opacity: ${(props) => (props.$dimmed ? 0.35 : 1)};
  transition: opacity ${SAVE_FADE_MS}ms ease;

  svg {
    display: block;
    width: 100%;
    height: 100%;
  }

  path {
    transition: ${transition.baseTransition};
  }

  ${breakpoints.mdDown} {
    width: ${(props) => (props.$inAllWines ? "13px" : getStarSize(props))};
    height: ${(props) => (props.$inAllWines ? "13px" : getStarSize(props))};
  }
  ${breakpoints.smDown} {
    width: ${(props) => (props.$inAllWines ? "9px" : getStarSize(props))};
    height: ${(props) => (props.$inAllWines ? "9px" : getStarSize(props))};
  }
`;

const ScrubBubble = styled.span`
  position: absolute;
  bottom: calc(100% + 10px);
  transform: translateX(-50%);
  padding: 3px 10px;
  border-radius: 12px;
  background-color: ${colors.black};
  color: ${colors.white};
  font-size: 14px;
  line-height: 1.2;
  white-space: nowrap;
  pointer-events: none;
  z-index: 2;
`;

interface StarProps {
  fillFraction: number;
  showHalfGuide?: boolean;
}

/* clipPath ids are document-global and many strips render at once
   (useId needs @types/react >= 18, hence the counter) */
let clipIdCounter = 0;

const Star = ({ fillFraction, showHalfGuide = false }: StarProps) => {
  const clipId = useMemo(() => {
    clipIdCounter += 1;
    return `wine-rating-star-clip-${clipIdCounter}`;
  }, []);
  return (
    <svg viewBox="-1 -1 27 25" focusable="false" aria-hidden="true">
      <path d={STAR_PATH} fill="none" stroke={colors.black} strokeWidth="1" />
      {showHalfGuide && (
        <>
          <clipPath id={clipId}>
            <path d={STAR_PATH} />
          </clipPath>
          {/* Faint centre guide advertising half-star ratings. Drawn under the
              fill, so it only shows on the unfilled part of a star. */}
          <line
            x1="12.5"
            y1="-1"
            x2="12.5"
            y2="24"
            stroke={colors.black}
            strokeWidth="1"
            strokeDasharray="2 1.5"
            opacity="0.35"
            clipPath={`url(#${clipId})`}
          />
        </>
      )}
      <path
        d={STAR_PATH}
        fill={colors.black}
        style={{ clipPath: `inset(-1px ${(1 - fillFraction) * 100}% -1px -1px)` }}
      />
    </svg>
  );
};

/* True while `active`, and for at least `minVisibleMs` after it first went
   true — the saving indicator's guard against sub-perceptual flashes. */
const useMinimumVisible = (active: boolean, minVisibleMs: number): boolean => {
  const [visible, setVisible] = useState(active);
  const shownAtRef = useRef(0);

  useEffect(() => {
    if (active) {
      shownAtRef.current = Date.now();
      setVisible(true);
      return undefined;
    }
    const remaining = shownAtRef.current + minVisibleMs - Date.now();
    if (remaining <= 0) {
      setVisible(false);
      return undefined;
    }
    const timer = setTimeout(() => setVisible(false), remaining);
    return () => clearTimeout(timer);
  }, [active, minVisibleMs]);

  return visible;
};

interface WineRatingTrackingContext {
  event: string;
  properties?: Record<string, unknown>;
}

interface WineRatingProps {
  rating?: number | null;
  onClickRating?: (score: number) => void;
  /** Saving in progress: fades the overlay spinner in over the stars. The strip
      stays interactive — a new choice mid-save just fires another commit and
      the last write wins. */
  pending?: boolean;
  largeIcon?: boolean;
  mediumIcon?: boolean;
  isInAllWines?: boolean;
  className?: string;
  /** Overrides the analytics event a commit fires (default: the overall
      product-rating event). Detail-rating strips pass their own event plus
      the attribute key so overall-rating analytics stay unpolluted. */
  trackingContext?: WineRatingTrackingContext;
}

/**
 * The shared star-rating strip. Read-only when no onClickRating is given
 * (fractional scores render as partly filled stars). When interactive:
 * tap picks a whole star, press-and-HOLD then slide scrubs in half-star
 * steps (with a value bubble) — a quick flick passes through to the page
 * or carousel scroll — and hover-capable pointers get per-half hover
 * preview plus click to commit.
 */
const WineRating = ({
  rating = 0,
  onClickRating,
  pending = false,
  largeIcon = false,
  mediumIcon = false,
  isInAllWines = false,
  className = "",
  trackingContext,
}: WineRatingProps) => {
  const { t } = useTranslation([I18N_NAMESPACES.COMMON]);

  const interactive = onClickRating != null;

  /* Drives the whole saving cross-fade (spinner + star dimming) so both hold
     together through the minimum display time. aria-busy stays on the real
     `pending` — assistive tech should track the save, not the animation. */
  const showSaving = useMinimumVisible(pending, MIN_SAVE_INDICATOR_MS);

  const hoverCapable = useMemo(
    () =>
      typeof window !== "undefined" && !!window.matchMedia?.("(hover: hover) and (pointer: fine)").matches,
    []
  );

  const rowRef = useRef<HTMLDivElement>(null);
  const pointerStateRef = useRef<{
    startX: number;
    startY: number;
    lastX: number;
    pointerId: number;
    scrubbing: boolean;
    abandoned: boolean;
  } | null>(null);
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /* Mirrors pointerStateRef.scrubbing for the non-passive touchmove listener. */
  const scrubActiveRef = useRef(false);
  const lastDetentRef = useRef<number | null>(null);
  const suppressClickRef = useRef(false);
  const mousePressInRowRef = useRef(false);

  const [scrub, setScrub] = useState<{ value: number; x: number } | null>(null);
  const [hoverValue, setHoverValue] = useState<number | null>(null);
  /* The last value the user committed. Kept as the display value so the stars
     don't flick back to the stale `rating` prop while the mutation and any
     refetches are still in flight. */
  const [committedValue, setCommittedValue] = useState<number | null>(null);

  /* Fresh server truth arriving through the prop retakes control of the display;
     without this, one commit would pin the stars to local state forever. */
  useEffect(() => {
    setCommittedValue(null);
  }, [rating]);

  /* Once the hold has engaged the scrub, stop the browser from turning further
     finger movement into scrolling. Must be a native non-passive listener —
     React's root-level touch listeners are passive, so their preventDefault
     is ignored. Before the hold, moves stay cancelable-untouched and flicks
     scroll the carousel/page natively. */
  useEffect(() => {
    const node = rowRef.current;
    if (!node || !interactive) {
      return undefined;
    }
    const blockScrollWhileScrubbing = (event: TouchEvent) => {
      if (scrubActiveRef.current && event.cancelable) {
        event.preventDefault();
      }
    };
    node.addEventListener("touchmove", blockScrollWhileScrubbing, { passive: false });
    return () => node.removeEventListener("touchmove", blockScrollWhileScrubbing);
  }, [interactive]);

  /* A pending hold must not fire after unmount. */
  useEffect(
    () => () => {
      if (holdTimerRef.current != null) {
        clearTimeout(holdTimerRef.current);
      }
    },
    []
  );

  const trackEvent = useTrackEvent();

  const scoreAt = (clientX: number): number | null => {
    const slots = rowRef.current?.querySelectorAll("[data-star-index]");
    if (!slots || slots.length === 0) {
      return null;
    }
    const first = slots[0].getBoundingClientRect();
    const last = slots[slots.length - 1].getBoundingClientRect();
    const stripWidth = last.right - first.left;
    if (stripWidth <= 0) {
      return null;
    }
    return positionToHalfStarScore((clientX - first.left) / stripWidth);
  };

  const scoreNearStrip = (clientX: number): number | null => {
    const slots = rowRef.current?.querySelectorAll("[data-star-index]");
    if (!slots || slots.length === 0) {
      return null;
    }
    const first = slots[0].getBoundingClientRect();
    const last = slots[slots.length - 1].getBoundingClientRect();
    if (clientX < first.left - TAP_SLOP_PX || clientX > last.right + TAP_SLOP_PX) {
      return null;
    }
    return scoreAt(clientX);
  };

  const scrubOffsetX = (clientX: number): number => {
    const rect = rowRef.current?.getBoundingClientRect();
    return rect ? clientX - rect.left : 0;
  };

  const commit = (score: number | null, method: string) => {
    if (score == null) {
      return;
    }
    setCommittedValue(score);
    onClickRating?.(score);
    /* After the rating handler: a tracking failure must never block the save. */
    try {
      trackEvent(trackingContext?.event ?? trackingEvents.TRACKING_EVENT_SELECTED_PRODUCT_RATING, {
        score,
        rating_method: method,
        ...trackingContext?.properties,
      });
    } catch {
      // Analytics-only failure; the rating itself has already been dispatched.
    }
  };

  const clearHoldTimer = () => {
    if (holdTimerRef.current != null) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  };

  /* The hold elapsed with the finger still (near enough) where it started:
     the gesture is now a scrub, not a scroll. */
  const engageScrub = () => {
    const pointerState = pointerStateRef.current;
    if (!pointerState || pointerState.abandoned || pointerState.scrubbing) {
      return;
    }
    pointerState.scrubbing = true;
    scrubActiveRef.current = true;
    rowRef.current?.setPointerCapture?.(pointerState.pointerId);
    navigator.vibrate?.(12);
    const score = scoreAt(pointerState.lastX);
    if (score != null) {
      lastDetentRef.current = score;
      setScrub({ value: score, x: scrubOffsetX(pointerState.lastX) });
    }
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    /* A new gesture invalidates any pending click suppression — a scrub never
       produces the compatibility click that would otherwise consume it, and the
       stale flag would swallow the next genuine mouse click. */
    suppressClickRef.current = false;
    /* Main button/finger/pen only — a right-click must keep its context menu. */
    if (event.button !== 0) {
      return;
    }
    pointerStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      pointerId: event.pointerId,
      scrubbing: false,
      abandoned: false,
    };
    lastDetentRef.current = null;
    clearHoldTimer();
    holdTimerRef.current = setTimeout(engageScrub, SCRUB_HOLD_MS);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const pointerState = pointerStateRef.current;
    if (!pointerState) {
      return;
    }
    pointerState.lastX = event.clientX;
    if (!pointerState.scrubbing) {
      const deltaX = event.clientX - pointerState.startX;
      const deltaY = event.clientY - pointerState.startY;
      if (
        !pointerState.abandoned &&
        (Math.abs(deltaX) > HOLD_CANCEL_SLOP_PX || Math.abs(deltaY) > HOLD_CANCEL_SLOP_PX)
      ) {
        /* Moved before the hold elapsed: this is a scroll. Hand it to the
           browser and commit nothing on release. */
        pointerState.abandoned = true;
        clearHoldTimer();
      }
      return;
    }
    const score = scoreAt(event.clientX);
    if (score == null) {
      return;
    }
    if (lastDetentRef.current !== score) {
      lastDetentRef.current = score;
      navigator.vibrate?.(8);
    }
    setScrub({ value: score, x: scrubOffsetX(event.clientX) });
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    clearHoldTimer();
    const pointerState = pointerStateRef.current;
    pointerStateRef.current = null;
    scrubActiveRef.current = false;
    if (!pointerState) {
      return;
    }
    if (pointerState.scrubbing) {
      suppressClickRef.current = true;
      commit(scrub?.value ?? scoreAt(event.clientX), trackingEventSelectedProductRatingMethods.SLIDE);
      setScrub(null);
      return;
    }
    /* Mouse releases that didn't scrub fall through to the click handler —
       committing here too would double-fire, and suppressing the click would
       swallow ordinary desktop clicks entirely. Only defer when that handler
       is actually attached: hover-incapable environments (real phones, and
       touch emulation where automation still emits mouse-type pointers) have
       no onClick, so their releases must commit right here. */
    if (event.pointerType === "mouse" && hoverCapable) {
      return;
    }
    suppressClickRef.current = true;
    if (pointerState.abandoned) {
      return;
    }
    const slot = (event.target as Element).closest?.("[data-star-index]");
    if (slot) {
      commit(Number(slot.getAttribute("data-star-index")), trackingEventSelectedProductRatingMethods.TAP);
      return;
    }
    const score = scoreNearStrip(event.clientX);
    commit(score == null ? null : Math.ceil(score), trackingEventSelectedProductRatingMethods.TAP);
  };

  const handlePointerCancel = () => {
    clearHoldTimer();
    pointerStateRef.current = null;
    scrubActiveRef.current = false;
    setScrub(null);
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    // Stop a press on the strip from anchoring a text selection
    event.preventDefault();
    mousePressInRowRef.current = true;
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.buttons === 0) {
      mousePressInRowRef.current = false;
    } else if (!mousePressInRowRef.current) {
      /* A drag that began outside the strip (text selection, card drag) is not
         a rating gesture and never commits on release — don't let the stars
         track it as if it were one. */
      setHoverValue(null);
      return;
    }
    setHoverValue(scoreNearStrip(event.clientX));
  };

  const handleMouseLeave = () => {
    setHoverValue(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    commit(hoverValue ?? scoreNearStrip(event.clientX), trackingEventSelectedProductRatingMethods.CLICK);
  };

  /* Android fires contextmenu on a long press, which would tear down the
     hold-to-scrub gesture. Only swallow it mid-gesture — a right-click never
     starts one (button guard in pointerdown), so its menu is unaffected. */
  const handleContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    if (pointerStateRef.current) {
      event.preventDefault();
    }
  };

  /* Keyboard slider semantics: arrows step half stars, Home/End jump. Each
     step commits — the strip already tolerates rapid re-commits (last write
     wins), so no separate confirm step is needed. */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const current = committedValue ?? rating ?? 0;
    let next: number | null = null;
    if (event.key === "ArrowRight" || event.key === "ArrowUp") {
      next = Math.min(MAX_RATING, current + 0.5);
    } else if (event.key === "ArrowLeft" || event.key === "ArrowDown") {
      next = Math.max(MIN_RATING, current - 0.5);
    } else if (event.key === "Home") {
      next = MIN_RATING;
    } else if (event.key === "End") {
      next = MAX_RATING;
    }
    if (next == null || next === current) {
      return;
    }
    event.preventDefault();
    commit(next, trackingEventSelectedProductRatingMethods.KEYBOARD);
  };

  const interactionProps = interactive
    ? {
        onPointerDown: handlePointerDown,
        onPointerMove: handlePointerMove,
        onPointerUp: handlePointerUp,
        onPointerCancel: handlePointerCancel,
        onContextMenu: handleContextMenu,
        ...(hoverCapable
          ? {
              onMouseDown: handleMouseDown,
              onMouseMove: handleMouseMove,
              onMouseLeave: handleMouseLeave,
              onClick: handleClick,
            }
          : {}),
      }
    : {};

  const displayValue = scrub?.value ?? hoverValue ?? committedValue ?? rating ?? 0;

  return (
    <Row
      ref={rowRef}
      className={className}
      $interactive={interactive}
      $pending={pending}
      /* Interactive strips are a slider: focusable, arrow keys step half
         stars, Home/End jump to the ends. Read-only strips stay an image. */
      role={interactive ? "slider" : "img"}
      aria-label={
        interactive ? t("common:starRatingAriaLabel") : `${formatRatingScore(displayValue)} / ${MAX_RATING}`
      }
      aria-busy={pending}
      {...(interactive
        ? {
            tabIndex: 0,
            "aria-valuemin": 0,
            "aria-valuemax": MAX_RATING,
            "aria-valuenow": displayValue,
            "aria-valuetext": `${formatRatingScore(displayValue)} / ${MAX_RATING}`,
            onKeyDown: handleKeyDown,
          }
        : {})}
      {...interactionProps}
    >
      <StarStrip>
        {Array.from({ length: MAX_RATING }, (_, index) => {
          const fillFraction = Math.min(1, Math.max(0, displayValue - index));
          return (
            <StarSlot
              key={index + 1}
              data-testid={`wine-rating-star-${index + 1}`}
              data-star-index={index + 1}
              data-fill={fillFraction}
              $large={largeIcon}
              $medium={mediumIcon}
              $inAllWines={isInAllWines}
              $dimmed={showSaving}
            >
              <Star fillFraction={fillFraction} showHalfGuide={interactive} />
            </StarSlot>
          );
        })}
        <SavingSpinner
          data-testid="rating-saving-spinner"
          data-visible={showSaving}
          $large={largeIcon}
          $visible={showSaving}
        />
      </StarStrip>
      {scrub != null && (
        <ScrubBubble style={{ left: `${scrub.x}px` }}>{formatRatingScore(scrub.value)}</ScrubBubble>
      )}
    </Row>
  );
};

export default WineRating;
